import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';
import { toast } from 'sonner';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isReviewer, setIsReviewer] = useState(false);
  const [profile, setProfile] = useState<{ avatar_url: string | null; avatar_url_200: string | null; avatar_url_80: string | null; display_name: string | null } | null>(null);

  // Session validation function
  const validateSession = useCallback(async () => {
    const { data: { session: currentSession }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('❌ Error validating session:', error);
      return;
    }
    
    if (!currentSession && user) {
      // UI shows logged in but session is invalid
      console.error('❌ Session mismatch detected - forcing logout');
      setUser(null);
      setSession(null);
      setIsAdmin(false);
      setIsReviewer(false);
      toast.error('Your session has expired. Please log in again.');
    }
    
    if (currentSession && !user) {
      // Session exists but UI shows logged out
      console.warn('⚠️ Session exists but user state is null - resyncing');
      setUser(currentSession.user);
      setSession(currentSession);
    }
  }, [user]);

  // Periodic session validation (every 5 minutes)
  useEffect(() => {
    const interval = setInterval(() => {
      validateSession();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [validateSession]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log(`🔐 Auth Event: ${event}`, {
          hasSession: !!session,
          emailVerified: session?.user?.email_confirmed_at ? true : false,
          expiresAt: session?.expires_at ? new Date(session.expires_at * 1000).toLocaleString() : null,
          userId: session?.user?.id
        });

        // If user is signing in but email not verified, sign them out
        if (event === 'SIGNED_IN' && session?.user && !session.user.email_confirmed_at) {
          console.warn('⚠️ Unverified user detected, forcing sign out');
          setTimeout(() => {
            supabase.auth.signOut();
            toast.error('Email not confirmed - Please check your email');
          }, 100);
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);
        
        if (event === 'SIGNED_OUT') {
          console.warn('⚠️ User signed out');
          setIsAdmin(false);
        }
        
        if (event === 'TOKEN_REFRESHED') {
          console.log('✅ Token refreshed successfully');
        }
        
        if (event === 'SIGNED_IN') {
          console.log('✅ User signed in successfully');
        }
        
        // Defer Supabase calls to prevent deadlock
        if (session?.user) {
          setTimeout(async () => {
            try {
              const { data: isAdminRole, error: adminError } = await supabase
                .rpc('has_role', { _user_id: session.user.id, _role: 'admin' });
              
              if (adminError) throw adminError;
              setIsAdmin(isAdminRole || false);
              
              const { data: isReviewerRole, error: reviewerError } = await supabase
                .rpc('has_role', { _user_id: session.user.id, _role: 'reviewer' });
              
              if (reviewerError) throw reviewerError;
              setIsReviewer(isReviewerRole || false);

              // Fetch user profile
              const { data: profileData, error: profileError } = await supabase
                .from('profiles')
                .select('avatar_url, avatar_url_200, avatar_url_80, display_name')
                .eq('user_id', session.user.id)
                .single();
              
              if (!profileError && profileData) {
                setProfile(profileData);
              }
            } catch (error) {
              console.error('Failed to check user roles:', error);
              setIsAdmin(false);
              setIsReviewer(false);
            } finally {
              // Set loading to false after roles are determined
              setLoading(false);
            }
          }, 0);
        } else {
          setIsAdmin(false);
          setIsReviewer(false);
          setProfile(null);
          setLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      // Loading state will be handled by onAuthStateChange callback
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    try {
      console.log('🚪 User initiated logout');
      
      // First, check if we actually have a session
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (!currentSession) {
        // Session already gone - just clean up local state
        console.log('⚠️ No active session found - cleaning up local state');
        setUser(null);
        setSession(null);
        setIsAdmin(false);
        toast.success('Logged out successfully');
        return;
      }
      
      // Session exists - proceed with normal logout
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        // Check if it's a "session not found" error (already logged out)
        if (error.message.includes('session') && error.message.includes('not found')) {
          console.warn('⚠️ Session already invalidated - cleaning up');
          setUser(null);
          setSession(null);
          setIsAdmin(false);
          toast.success('Logged out successfully');
        } else {
          // Some other error - show it to user
          console.error('❌ Logout failed:', error);
          toast.error(`Logout failed: ${error.message}`);
        }
      } else {
        console.log('✅ Logged out successfully');
        toast.success('Logged out successfully');
      }
    } catch (err) {
      console.error('❌ Unexpected error during logout:', err);
      // Even on unexpected error, clean up local state
      setUser(null);
      setSession(null);
      setIsAdmin(false);
      toast.error('Logged out with errors');
    }
  };

  const refreshSession = async () => {
    try {
      console.log('🔄 Manually refreshing session');
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        console.error('❌ Session refresh failed:', error);
        toast.error('Failed to refresh session');
        return false;
      }
      console.log('✅ Session refreshed manually');
      return true;
    } catch (err) {
      console.error('❌ Unexpected error refreshing session:', err);
      return false;
    }
  };

  return {
    user,
    session,
    loading,
    isAdmin,
    isReviewer,
    profile,
    signOut,
    refreshSession,
    validateSession,
  };
};